<script>
  import { createEventDispatcher, onMount, tick } from "svelte";
  import axios from "axios";
  import ChatTextBox from "./chat/ChatTextBox.svelte";
  import ChatBubbleBot from "./chat/ChatBubbleBot.svelte";
  import ChatBubbleUser from "./chat/ChatBubbleUser.svelte";
  import Button from "./base/Button.svelte";
  import Closer from "./base/Closer.svelte";
  import { getHost } from "../utils/sendwhatsapp_rewrite_chabi";
  import { v4 as uuidv4 } from "uuid";
  import { addHttpsToUrl, formatRichText } from "../utils/utilities";
  import Modal from "../components/Modal.svelte";
  import Form from "../components/Form.svelte";
  import { sendform, fileUpload } from "../utils/sendwhatsapp_rewrite_chabi";
  import { getOrCreateVisitorId } from "../utils/localStorage";
  import { currentLanguage } from "../store";
  import translations from "../translations";

  const uuid = uuidv4();

  export let data;
  export let activePerson;
  export let leadId;

  let visitorId = null;
  let visitorProfile = null;

  let chatList = [{ id: 1, botText: data.faqsChatBotQuestionText ?? "Hello, how can I help you?", userText: "", userAttachments: [] }];
  let faqs = [];
  let isSolved = true;
  let aiVersionNotice = null;
  let handoffRequested = false;
  let handoffLanguage = null;

  // attachments queued for the next send (already uploaded to backend)
  let pendingAttachments = [];
  let attachmentUploading = false;
  let fileInputEl;
  let nextAttachmentLocalId = 1;
  const ATTACHMENT_ACCEPT = "image/*,.heic,.pdf,.doc,.docx,.xls,.xlsx";
  const ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

  // estado para el soft follow-up
  let hasPositiveAnswer = false; // pasa a true tras la PRIMERA respuesta positiva
  let formModalOpen = null; // trigger para abrir el Modal
  let formitems = { items: [] }; // espejo local para el formulario en modal

  // estado para el paywall de IA: cuando searchfaq-ai devuelve payment_link,
  // se muestra el pop-up de suscripción. No bloqueamos el envío de forma
  // permanente: cada mensaje vuelve a llamar a searchfaq-ai y es el backend
  // quien decide (si ya pagó, deja de mandar payment_link y responde normal).
  let paymentLink = null;
  let paymentModalVisible = false;

  // textos hardcodeados con fallback a la traducción configurada si existe
  $: paywallTitle = data.faqsChatBotPaymentTitle ?? "Suscríbete para continuar";
  $: paywallText =
    data.faqsChatBotPaymentText ?? "Has alcanzado el límite de mensajes gratuitos. Suscríbete para seguir usando el asistente.";
  $: paywallButtonText = data.faqsChatBotPaymentButton ?? "Suscribirme";

  $: formitems = activePerson?.form ?? { items: [] };

  $: fullScreenMobile = data.expandFullScreenMobile;

  // Atajo a la primera faqGrp
  $: faq = activePerson?.faqGrps?.[0] ?? {};

  $: commonQuestions = (Array.isArray(faq.commonQuestions) ? faq.commonQuestions : [])
    .map((item) => (typeof item === "string" ? { question: item, answer: null } : item))
    .filter((item) => typeof item?.question === "string" && item.question.trim());
  $: showCommonChips = commonQuestions.length > 0 && chatList.length === 1 && !chatList[0].userText;

  // attachments only supported on the standard /api/searchfaq-ai flow; aiCollectData uses a different endpoint
  $: attachmentsEnabled = !activePerson?.faqGrps?.[0]?.aiCollectData;
  $: canOpenHandoffForm = (activePerson?.form?.items?.length ?? 0) > 0;
  $: handoffButtonLanguage = normalizeHandoffLanguage(handoffLanguage) ?? $currentLanguage;
  $: handoffButtonText =
    translations.aiHumanHandoffButton[handoffButtonLanguage] ?? translations.aiHumanHandoffButton.en;

  // Reglas:
  // 1) hideWhatsappButton => jamás se muestra
  // 2) Si NO está hide y NO tiene showWhatsappBtnWhenUnknown => se muestra
  // 3) Si además tiene showWhatsappBtnWhenUnknown => solo se muestra si isSolved === false
  $: showWhatsappBtn = faq.hideWhatsappButton ? false : faq.showWhatsappBtnWhenUnknown ? isSolved === false : true;

  $: softFollowUpFooter =
    Boolean(faq?.softFollowUp) &&
    hasPositiveAnswer &&
    !activePerson?.formBeforeFaqs &&
    (activePerson?.form?.items?.length ?? 0) > 0 &&
    activePerson?.online === true;

  $: showInlineWhatsappBtn = showWhatsappBtn && faq.showWhatsappBtnWhenUnknown && isSolved === false;
  $: showFooterWhatsappBtn = showWhatsappBtn && !showInlineWhatsappBtn;

  $: showFooterBtnFinal = (!handoffRequested || !canOpenHandoffForm) && (showFooterWhatsappBtn || softFollowUpFooter);

  const dispatch = createEventDispatcher();

  onMount(async () => {
    dispatch("thread-id", { threadId: uuid });

    visitorId = getOrCreateVisitorId(data.id);

    try {
      const res = await axios.get(`${getHost()}/ai/visitor/${activePerson.id}?visitorId=${visitorId}`);
      visitorProfile = res.data;

      if (visitorProfile?.name && visitorProfile.visitCount > 1) {
        const base = data.faqsChatBotQuestionText ?? "Hello, how can I help you?";
        chatList[0] = { ...chatList[0], botText: `${visitorProfile.name}, ${base}` };
        chatList = chatList;
      }
      dispatch("visitor-profile", { profile: visitorProfile });
    } catch (_) {
      // fail silently, default greeting stays
    }
  });

  // Abre el pop-up de suscripción con el link de pago recibido.
  function triggerPaywall(link) {
    paymentLink = link;
    paymentModalVisible = true;
    scrollToBottom();
  }

  // Cierra el pop-up. El input vuelve a estar disponible para seguir preguntando;
  // si el backend sigue devolviendo payment_link, el pop-up reaparecerá solo.
  function closePaywall() {
    paymentModalVisible = false;
  }

  function getAiAnswer(payload) {
    const answerPayload = payload?.answer;
    const answerText = typeof answerPayload === "string" ? answerPayload : answerPayload?.answer ?? answerPayload?.text ?? "";
    const freeText = payload?.version_gratuita ?? answerPayload?.version_gratuita;
    const premiumText = payload?.version_premium ?? answerPayload?.version_premium;
    const paymentLink = payload?.payment_link_1 ?? answerPayload?.payment_link_1;
    const paymentText = payload?.payment_text_1 ?? answerPayload?.payment_text_1;
    const paywallLink = payload?.payment_link ?? answerPayload?.payment_link;

    if (typeof freeText === "string" && freeText.trim()) {
      return { text: answerText, notice: { text: freeText, variant: "free", paymentLink, paymentText }, paywallLink };
    }
    if (typeof premiumText === "string" && premiumText.trim()) {
      return { text: answerText, notice: { text: premiumText, variant: "premium", paymentLink, paymentText }, paywallLink };
    }

    return { text: answerText, notice: null, paywallLink };
  }

  function normalizeHandoffLanguage(language) {
    if (typeof language !== "string") return null;

    const baseLanguage = language.trim().toLowerCase().replace("_", "-").split("-")[0];
    const aliases = { zh: "ch", cn: "ch" };
    const normalizedLanguage = aliases[baseLanguage] ?? baseLanguage;

    return translations.aiHumanHandoffButton[normalizedLanguage] ? normalizedLanguage : null;
  }

  function applyHandoffResponse(response) {
    if (response?.handoff_requested !== true) return;

    handoffRequested = true;
    handoffLanguage = response?.conversation_language ?? null;
  }

  async function setVal(v) {
    const questionText = (v?.detail ?? "").toString();
    const normalizeQuestion = (text) => text.trim().replace(/\s+/g, " ").toLowerCase();
    const commonQuestion = commonQuestions.find(
      (item) =>
        normalizeQuestion(item.question) === normalizeQuestion(questionText) &&
        typeof item.answer === "string" &&
        item.answer.trim()
    );

    // Keep sends with attachments on the backend flow so files are processed.
    if (commonQuestion && !attachmentUploading && pendingAttachments.length === 0) {
      chatList[chatList.length - 1].userText = questionText;
      chatList = [
        ...chatList,
        { id: chatList.length + 1, botText: commonQuestion.answer, userText: "", userAttachments: [] },
      ];
      isSolved = true;
      hasPositiveAnswer = true;
      scrollToBottom();
      return;
    }

    if (activePerson.faqGrps[0].aiCollectData) {
      setValWithCollectedData(v, activePerson.id);
    } else {
      let objDiv = document.getElementById("messageBox");
      objDiv.scrollTop = objDiv.scrollHeight;
      const text = (v?.detail ?? "").toString();
      const readyAttachments = pendingAttachments.filter((a) => a.uuid && !a.uploading);
      if (text.trim() === "" && readyAttachments.length === 0) return;
      if (attachmentUploading) return;
      let param = {
        search: text.trim(),
        previous: chatList[chatList.length - 1],
      };

      param["whatsapp"] = activePerson.id;
      param["conversation"] = uuid;
      param["lead"] = leadId || null;
      param["visitorId"] = visitorId;
      if (readyAttachments.length) {
        param["attachments"] = readyAttachments.map((a) => ({
          uuid: a.uuid,
          filename: a.filename,
          mimeType: a.mimeType,
          size: a.size,
        }));
      }

      // show user text + attachments in chat just after user sends it
      chatList[chatList.length - 1].userText = text;
      chatList[chatList.length - 1].userAttachments = readyAttachments.map((a) => ({
        uuid: a.uuid,
        filename: a.filename,
        mimeType: a.mimeType,
      }));
      pendingAttachments = [];

      await axios
        .post(getHost() + "/api/searchfaq-ai", param)
        .then((response) => {
          faqs = response.data;
          applyHandoffResponse(faqs);

          // update user text with the answer
          chatList[chatList.length - 1].userText = text;

          const aiAnswer = getAiAnswer(faqs);

          // payment_link es el paywall viejo. payment_link_1 es sólo un CTA y no bloquea la respuesta.
          if (aiAnswer.paywallLink) {
            triggerPaywall(aiAnswer.paywallLink);
            return;
          }

          if (aiAnswer.notice) {
            aiVersionNotice = aiAnswer.notice;
          }

          if (aiAnswer.notice && !aiAnswer.text) {
            chatList.push({
              id: chatList.length + 1,
              botText: "",
              userText: "",
              userAttachments: [],
            });
            if (!hasPositiveAnswer) hasPositiveAnswer = true;
            isSolved = true;
          } else if ((aiAnswer.notice && aiAnswer.text) || (faqs && faqs.relevance && aiAnswer.text && faqs.answer?.relevance !== 0)) {
            chatList.push({
              id: chatList.length + 1,
              botText: aiAnswer.text,
              userText: "",
              userAttachments: [],
            });
            // si es la primera positiva, lo marcamos
            if (!hasPositiveAnswer) hasPositiveAnswer = true;

            isSolved = true;
          } else {
            // respuesta unknown del bot
            chatList.push({
              id: chatList.length + 1,
              botText: data.faqsChatBotNotSolvedText ?? "Sorry, I don't have an answer for that, what else can I help you with?",
              userText: "",
              userAttachments: [],
            });
            isSolved = false;
            animateWhatsAppButton();
          }

          // if (faqs.extra_question){
          //   //setTimeout(() => {
          //     chatList.push({
          //       id: chatList.length+ 1,
          //       botText: faqs.extra_question,
          //       userText: ""
          //     });
          //   //}, 1000)
          // }
          let objDiv = document.getElementById("messageBox");
          scrollToBottom();
        })
        .catch((error) => {
          alert(error);
        });
    }
  }

  async function setValWithCollectedData(v, widgetId) {
    let objDiv = document.getElementById("messageBox");
    objDiv.scrollTop = objDiv.scrollHeight;
    if (v.detail.trim() == "") return;
    let text = v.detail;
    let param = {
      question: text.trim(),
      threadId: uuid,
      visitorId: visitorId,
    };

    // Muestra el texto del usuario en el chat inmediatamente
    chatList[chatList.length - 1].userText = text;
    try {
      const response = await axios.post(`${getHost()}/ai/faq-data-extractor-receiver/${widgetId}`, param, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
      });

      const data = response.data;
      applyHandoffResponse(data);

      // límite de mensajes IA alcanzado: bloquea el bot y muestra el pop-up de suscripción
      if (data.payment_link) {
        triggerPaywall(data.payment_link);
        return;
      }

      // Verifica si hay una respuesta del bot
      if (data.chat_bot_answer_about_datatext) {
        chatList = [
          ...chatList,
          {
            id: chatList.length + 1,
            botText: data.chat_bot_answer_about_datatext,
            userText: "",
          },
        ];
      }

      // Verifica si hay una pregunta siguiente
      if (data.chat_bot_next_question) {
        setTimeout(() => {
          chatList = [
            ...chatList,
            {
              id: chatList.length + 1,
              botText: data.chat_bot_next_question,
              userText: "",
            },
          ];
          isSolved = true;
          scrollToBottom();
        }, 2000);
      } else {
        isSolved = false;
        animateWhatsAppButton();
      }
      // Asegúrate de que el chat se desplaza al fondo
      scrollToBottom();
    } catch (error) {
      alert("Hubo un error procesando tu solicitud.");
    }
  }

  function handleFooterButtonClick() {
    // Si aplica el soft follow-up: abrimos modal de Form
    if (softFollowUpFooter) {
      formModalOpen = Math.random();
      return;
    }
    if (activePerson?.online === true) {
      dispatch("problem-not-solved");
    } else {
      dispatch("leave-message");
    }
  }

  function openHandoffForm() {
    if (!canOpenHandoffForm) return;
    dispatch("open-lead-form");
  }

  async function sf(event) {
    try {
      const lead = await sendform(activePerson, event, data.redirectUrl, data.sellLeads, data.id, uuid);
      if (lead && !leadId) leadId = lead;
      if (data?.closeOnSend) formModalOpen = null;
    } catch (e) {
      alert("Error enviando el formulario.");
    }
  }

  function triggerFilePicker() {
    if (attachmentUploading) return;
    fileInputEl?.click();
  }

  async function onFileSelected(event) {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (!files.length) return;

    for (const file of files) {
      if (file.size > ATTACHMENT_MAX_BYTES) {
        alert(`${file.name}: max 20MB`);
        continue;
      }
      const localId = nextAttachmentLocalId++;
      pendingAttachments = [
        ...pendingAttachments,
        { localId, uuid: null, filename: file.name, mimeType: file.type, size: file.size, uploading: true },
      ];
      attachmentUploading = true;
      try {
        const res = await fileUpload(file, activePerson.id);
        const uuid = res?.data?.id;
        if (!uuid) throw new Error("upload failed");
        pendingAttachments = pendingAttachments.map((a) => (a.localId === localId ? { ...a, uuid, uploading: false } : a));
      } catch (err) {
        pendingAttachments = pendingAttachments.filter((a) => a.localId !== localId);
        alert(`${file.name}: upload failed`);
      } finally {
        attachmentUploading = pendingAttachments.some((a) => a.uploading);
      }
    }
  }

  function removeAttachment(localId) {
    pendingAttachments = pendingAttachments.filter((a) => a.localId !== localId);
    attachmentUploading = pendingAttachments.some((a) => a.uploading);
  }

  const animateWhatsAppButton = async () => {
    await tick();
    const whatsappBtn = document.getElementById("whatsapp-btn");
    whatsappBtn?.classList.add("shake-btn");
    setTimeout(() => {
      whatsappBtn?.classList.remove("shake-btn");
    }, 2000);
  };

  /**
   * Scrolls to the bottom of the chat box.
   */
  const scrollToBottom = () => {
    tick().then(() => {
      const messageBox = document.getElementsByClassName("chatwith-chat-panel")[0];
      if (messageBox) {
        messageBox.scrollTo({
          top: messageBox.scrollHeight,
          behavior: "smooth",
        });
      }
    });
  };
</script>

{#if aiVersionNotice}
  <div class="chatwith-ai-version-notice chatwith-ai-version-notice-{aiVersionNotice.variant}">
    {@html formatRichText(aiVersionNotice.text)}
    {#if aiVersionNotice.paymentLink}
      <a class="chatwith-ai-version-notice-link" href={aiVersionNotice.paymentLink} target="_blank" rel="noopener noreferrer">
        {aiVersionNotice.paymentText || "Acceder a Premium"}
      </a>
    {/if}
  </div>
{/if}

<div class="chatwith-chat chatwith-faqschat" class:fullScreenMobile>
  <div class="chatwith-chat-panel">
    <div class="chatwith-chat-content">
      <div class="chatwith-chat-messages" id="messageBox">
        {#each chatList as element, i (element.id)}
          {#if element.botText}
            <div class="scale-in-left">
              <ChatBubbleBot text={element.botText} showDot={true} />
            </div>
          {/if}
          {#if i === 0 && showCommonChips}
            <div class="chatwith-faqschat-suggestions">
              {#each commonQuestions as item}
                <button type="button" class="chatwith-faqschat-suggestion" on:click={() => setVal({ detail: item.question })}>
                  {item.question}
                </button>
              {/each}
            </div>
          {/if}
          {#if showInlineWhatsappBtn && i === chatList.length - 1}
            <div class="whatsapp-inline">
              {#if activePerson?.online === true}
                <Button
                  on:click={() => dispatch("problem-not-solved")}
                  buttonText={data?.translateChatWithAgent ?? "WhatsApp"}
                  showIcon={false}
                  id="whatsapp-btn"
                />
              {:else}
                <Button
                  on:click={() => dispatch("leave-message")}
                  buttonText={data?.leaveMessage ?? "Leave a message"}
                  showIcon={false}
                  id="whatsapp-btn"
                />
              {/if}
            </div>
          {/if}
          {#if element.userText || (element.userAttachments && element.userAttachments.length)}
            <div class="scale-in-right">
              {#if element.userText}
                <ChatBubbleUser bubbleData={{ val: element.userText }} />
              {/if}
              {#if element.userAttachments && element.userAttachments.length}
                <div class="chatwith-ai-user-attachments">
                  {#each element.userAttachments as att (att.uuid)}
                    <a
                      class="chatwith-ai-user-attachment"
                      href={`${getHost()}/file-user/${att.uuid}`}
                      target="_blank"
                      rel="noopener"
                      title={att.filename}
                    >
                      {#if att.mimeType && att.mimeType.startsWith("image/")}
                        <img src={`${getHost()}/file-user/${att.uuid}`} alt={att.filename} />
                      {:else}
                        <span class="chatwith-ai-user-attachment-name">{att.filename}</span>
                      {/if}
                    </a>
                  {/each}
                </div>
              {/if}
            </div>
          {/if}
        {/each}
        {#if handoffRequested && canOpenHandoffForm}
          <div class="chatwith-ai-handoff-cta">
            <Button on:click={openHandoffForm} buttonText={handoffButtonText} showIcon={false} />
          </div>
        {/if}
      </div>
    </div>
  </div>
  {#if data.widgetType === "bigperson"}
    <div class="chatwith-chat-footer-bigperson">
      {#if attachmentsEnabled && pendingAttachments.length}
        <div class="chatwith-ai-pending-attachments">
          {#each pendingAttachments as att (att.localId)}
            <span class="chatwith-ai-pending-chip" title={att.filename}>
              {#if att.mimeType && att.mimeType.startsWith("image/") && !att.uploading}
                <img src={`${getHost()}/file-user/${att.uuid}`} alt={att.filename} />
              {:else}
                <span class="chatwith-ai-pending-chip-name">{att.filename}</span>
              {/if}
              {#if att.uploading}
                <span class="chatwith-ai-pending-chip-spinner">…</span>
              {:else}
                <button type="button" class="chatwith-ai-pending-chip-remove" on:click={() => removeAttachment(att.localId)} aria-label="Remove">×</button>
              {/if}
            </span>
          {/each}
        </div>
      {/if}
      <div class="chatwith-ai-input-row">
        {#if attachmentsEnabled}
          <button type="button" class="chatwith-ai-attach-btn" on:click={triggerFilePicker} disabled={attachmentUploading} aria-label="Attach file">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input type="file" bind:this={fileInputEl} on:change={onFileSelected} accept={ATTACHMENT_ACCEPT} multiple style="display:none" />
        {/if}
        <div class="chatwith-ai-input-row-textbox">
          <ChatTextBox on:message={setVal} fullScreenMobile={data.expandFullScreenMobile} translateChatAnswer={data.translateChatAnswer} multiline={true} />
        </div>
      </div>
      {#if showFooterBtnFinal}
        <div class="chatwith-faqschat-buttons">
          <Button on:click={handleFooterButtonClick} buttonText={data.translateChatWithAgent ?? "WhatsApp"} showIcon={true} id="whatsapp-btn" />
        </div>
      {/if}
      {#if data.showBrandInWidget}
        <div class="chatwith-brand" style="font-size: 13px; font-weight:600; margin-top: 10px; color: #666; text-align: center">
          {#if data.brandNameUrl}
            <span><a style="color: blueviolet; font-size: 13px;" target="_blank" href={addHttpsToUrl(data.brandNameUrl)}>{data.brandName}</a></span>
          {:else}
            <span style="font-size: 13px;">{data.brandName}</span>
          {/if}
        </div>
      {/if}
    </div>
  {/if}
</div>

{#if data.widgetType !== "bigperson"}
  <div class="chatwith-chat " style="background: #F0F0F0;">
    <div class="chatwith-chat-footer">
      {#if attachmentsEnabled && pendingAttachments.length}
        <div class="chatwith-ai-pending-attachments">
          {#each pendingAttachments as att (att.localId)}
            <span class="chatwith-ai-pending-chip" title={att.filename}>
              {#if att.mimeType && att.mimeType.startsWith("image/") && !att.uploading}
                <img src={`${getHost()}/file-user/${att.uuid}`} alt={att.filename} />
              {:else}
                <span class="chatwith-ai-pending-chip-name">{att.filename}</span>
              {/if}
              {#if att.uploading}
                <span class="chatwith-ai-pending-chip-spinner">…</span>
              {:else}
                <button type="button" class="chatwith-ai-pending-chip-remove" on:click={() => removeAttachment(att.localId)} aria-label="Remove">×</button>
              {/if}
            </span>
          {/each}
        </div>
      {/if}
      <div class="chatwith-ai-input-row">
        {#if attachmentsEnabled}
          <button type="button" class="chatwith-ai-attach-btn" on:click={triggerFilePicker} disabled={attachmentUploading} aria-label="Attach file">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
          </button>
          <input type="file" bind:this={fileInputEl} on:change={onFileSelected} accept={ATTACHMENT_ACCEPT} multiple style="display:none" />
        {/if}
        <div class="chatwith-ai-input-row-textbox">
          <ChatTextBox
            on:message={setVal}
            hasBorder={data.widgetType === "bigperson"}
            fullScreenMobile={data.expandFullScreenMobile}
            translateChatAnswer={data.translateChatAnswer}
            multiline={true}
          />
        </div>
      </div>
      {#if showFooterBtnFinal}
        <div class="chatwith-faqschat-buttons">
          <Button on:click={handleFooterButtonClick} buttonText={data.translateChatWithAgent ?? "WhatsApp"} showIcon={true} id="whatsapp-btn" />
        </div>
      {/if}
      {#if data.showBrandInWidget}
        <div class="chatwith-brand" style="font-size: 13px; font-weight:600; margin-top: 10px; color: #666; text-align: center">
          {#if data.brandNameUrl}
            <span><a style="color: blueviolet; font-size: 13px;" target="_blank" href={addHttpsToUrl(data.brandNameUrl)}>{data.brandName}</a></span>
          {:else}
            <span style="font-size: 13px;">{data.brandName}</span>
          {/if}
        </div>
      {/if}
    </div>
  </div>
{/if}

<Modal isOpen={formModalOpen}>
  <Form on:callmex={sf} on:close={() => (formModalOpen = null)} {data} person={activePerson} {formitems} noclose={false} isBigperson={false} />
</Modal>

{#if paymentModalVisible}
  <div class="modal">
    <div class="modal-content">
      <div class="close-modal" on:click={closePaywall}>
        <svg
          aria-hidden="true"
          focusable="false"
          data-prefix="fas"
          data-icon="times"
          class="svg-inline--fa fa-times fa-w-11"
          role="img"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 352 512"
          ><path
            fill="#000"
            d="M242.72 256l100.07-100.07c12.28-12.28 12.28-32.19 0-44.48l-22.24-22.24c-12.28-12.28-32.19-12.28-44.48 0L176 189.28 75.93 89.21c-12.28-12.28-32.19-12.28-44.48 0L9.21 111.45c-12.28 12.28-12.28 32.19 0 44.48L109.28 256 9.21 356.07c-12.28 12.28-12.28 32.19 0 44.48l22.24 22.24c12.28 12.28 32.2 12.28 44.48 0L176 322.72l100.07 100.07c12.28 12.28 32.2 12.28 44.48 0l22.24-22.24c12.28-12.28 12.28-32.19 0-44.48L242.72 256z"
          /></svg
        >
      </div>
      <div class="chatwith-ai-paywall">
        <div class="chatwith-ai-paywall-title">{paywallTitle}</div>
        <div class="chatwith-ai-paywall-text">{paywallText}</div>
        {#if paymentLink}
          <a class="chatwith-ai-paywall-btn" href={paymentLink} target="_blank" rel="noopener noreferrer" on:click={closePaywall}>{paywallButtonText}</a>
        {/if}
      </div>
    </div>
  </div>
{/if}

<style>
  .chatwith-ai-input-row {
    display: flex;
    align-items: flex-end;
    gap: 6px;
    width: 100%;
  }

  .chatwith-ai-version-notice {
    margin: 8px 10px 10px;
    padding: 12px 14px;
    border-radius: 8px;
    font-size: 14px;
    line-height: 1.35;
    text-align: left;
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .chatwith-ai-version-notice :global(p) {
    margin: 0 0 8px;
  }

  .chatwith-ai-version-notice :global(p:last-child) {
    margin-bottom: 0;
  }

  .chatwith-ai-version-notice-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-top: 10px;
    padding: 8px 12px;
    border-radius: 6px;
    background: #111827;
    color: #ffffff;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    text-decoration: none;
  }

  .chatwith-ai-version-notice-link:hover {
    background: #000000;
  }

  .chatwith-ai-version-notice-free {
    background: #eef6ff;
    border: 1px solid #b8d8f4;
    color: #14324a;
    box-shadow: 0 2px 8px rgba(33, 103, 160, 0.12);
  }

  .chatwith-ai-version-notice-premium {
    background: #fff8df;
    border: 1px solid #edc95d;
    color: #3f2f05;
    box-shadow: 0 3px 12px rgba(139, 104, 12, 0.18);
  }

  .chatwith-ai-paywall {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 14px;
    padding: 12px 8px 4px;
  }

  .chatwith-ai-paywall-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
  }

  .chatwith-ai-paywall-text {
    font-size: 14px;
    line-height: 1.4;
    color: #555;
  }

  .chatwith-ai-paywall-btn {
    display: inline-block;
    margin-top: 4px;
    padding: 10px 24px;
    border-radius: 8px;
    background: #4f46e5;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
  }

  .chatwith-ai-paywall-btn:hover {
    background: #4338ca;
  }

  .chatwith-ai-input-row-textbox {
    flex: 1;
    min-width: 0;
  }

  .chatwith-ai-attach-btn {
    flex: 0 0 auto;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: #666;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0;
  }

  .chatwith-ai-attach-btn:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.06);
    color: #333;
  }

  .chatwith-ai-attach-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .chatwith-ai-pending-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 8px;
    margin-bottom: 4px;
  }

  .chatwith-ai-pending-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 4px 6px;
    max-width: 180px;
    font-size: 12px;
    color: #333;
  }

  .chatwith-ai-pending-chip img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    display: block;
  }

  .chatwith-ai-pending-chip-name {
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .chatwith-ai-pending-chip-remove {
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    color: #888;
    padding: 0 2px;
  }

  .chatwith-ai-pending-chip-remove:hover {
    color: #c00;
  }

  .chatwith-ai-pending-chip-spinner {
    font-size: 14px;
    color: #888;
  }

  .chatwith-ai-user-attachments {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    justify-content: flex-end;
    margin-top: 6px;
    margin-bottom: 16px;
  }

  .chatwith-ai-user-attachment {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 4px 6px;
    max-width: 200px;
    font-size: 12px;
    color: #333;
    text-decoration: none;
  }

  .chatwith-ai-user-attachment img {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 6px;
    display: block;
    border: 1px solid #ddd;
  }

  .chatwith-ai-user-attachment-name {
    max-width: 160px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
</style>
